import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/lib/api';
import { X, Sparkle, ShoppingBag } from '@phosphor-icons/react';

const TIER_LABELS = {
  mood_burst: 'Mood',
  skin:       'Skin',
  eyes:       'Eyes',
  whisper:    'Whisper',
  origin:     'Origin',
};

/**
 * CosmeticsDrawer — slide-up panel listing this user's owned cosmetics
 * grouped by kind, with one-tap "Apply" to the current bubble. A
 * "More in the Shop" link sends them to /shop for impulse buys.
 */
const CosmeticsDrawer = ({ open, onClose, bubble, onApplied }) => {
  const [owned, setOwned] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get('/me/cosmetics')
      .then(({ data }) => setOwned(data.cosmetics || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  const apply = async (inst) => {
    try {
      await api.post(`/bubbles/${bubble.id}/cosmetics/apply?instance_id=${inst.instance_id}`);
      toast.success(`${inst.name} is now part of ${bubble.name}`);
      onApplied?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'could not apply');
    }
  };

  const groups = {};
  owned.forEach((c) => {
    const k = c.kind || 'other';
    if (!groups[k]) groups[k] = [];
    groups[k].push(c);
  });

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[250] bg-black/85 backdrop-blur grid place-items-end sm:place-items-center"
          onClick={onClose}
          data-testid="cosmetics-drawer"
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="glass rounded-t-3xl sm:rounded-3xl w-full sm:max-w-lg max-h-[80vh] overflow-y-auto p-6"
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-bx-cyan">decorate · {bubble.name}</div>
                <div className="font-display text-2xl font-black mt-1">Your closet</div>
              </div>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 text-white/60">
                <X size={16} weight="bold" />
              </button>
            </div>

            {loading ? (
              <div className="text-white/60 text-sm py-6">opening drawers…</div>
            ) : owned.length === 0 ? (
              <div className="py-6 text-center">
                <div className="text-white/55 text-sm">Your closet is empty.</div>
                <div className="text-white/60 text-xs mt-1 italic">Bubbles aren't born with their best clothes.</div>
                <Link
                  to="/shop"
                  data-testid="drawer-go-shop"
                  className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 rounded-full btn-volt text-xs font-bold">
                  <ShoppingBag size={14} weight="fill" /> Visit Adorn
                </Link>
              </div>
            ) : (
              <>
                {Object.entries(groups).map(([kind, items]) => (
                  <div key={kind} className="mt-4">
                    <div className="text-[10px] uppercase tracking-widest text-white/60 mb-2">{TIER_LABELS[kind] || kind}</div>
                    <div className="grid grid-cols-2 gap-2">
                      {items.map((c) => {
                        const isApplied = bubble.applied_cosmetics?.[c.kind]?.instance_id === c.instance_id;
                        return (
                          <button
                            key={c.instance_id}
                            data-testid={`drawer-apply-${c.instance_id}`}
                            onClick={() => apply(c)}
                            className={`text-left p-3 rounded-2xl glass-hi hover:bg-white/10 transition border ${
                              isApplied ? 'border-bx-cyan/60' : 'border-transparent'
                            }`}>
                            <div className="font-bold text-sm">{c.name}</div>
                            <div className="text-[11px] italic text-white/65">"{c.tagline}"</div>
                            {c.expires_at && (
                              <div className="text-[10px] uppercase tracking-widest text-bx-volt mt-1">
                                until {new Date(c.expires_at).toLocaleDateString()}
                              </div>
                            )}
                            {isApplied && (
                              <div className="text-[10px] uppercase tracking-widest text-bx-cyan mt-1 font-bold flex items-center gap-1">
                                <Sparkle size={10} weight="fill" /> on now
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                <Link
                  to="/shop"
                  className="mt-6 inline-flex items-center justify-center gap-1.5 w-full px-4 py-3 rounded-full glass-hi hover:bg-white/10 text-xs font-bold uppercase tracking-widest">
                  <ShoppingBag size={14} weight="bold" /> Find more in Adorn
                </Link>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CosmeticsDrawer;
